<template>
  <div class="error-logs">
    <div v-if="loading" class="text-center pa-5">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <p class="mt-3">Carregando logs...</p>
    </div>

    <div v-else>
      <div class="titulo text-center elevation-1 mb-4">
        <h3>Monitoramento de Erros</h3>
      </div>

      <!-- Filtros -->
      <v-card class="mb-4 pa-4">
        <v-row>
          <v-col cols="12" md="3">
            <v-select
              v-model="periodPreset"
              :items="periodOptions"
              label="Período"
              @change="onPeriodChange"
            ></v-select>
          </v-col>
          <v-col cols="12" md="2" v-if="periodPreset === 'custom'">
            <v-text-field
              v-model="safeFilters.date_from"
              label="Data de"
              type="date"
              dense
              outlined
              hide-details
              @change="applyFilters"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="2" v-if="periodPreset === 'custom'">
            <v-text-field
              v-model="safeFilters.date_to"
              label="Data até"
              type="date"
              dense
              outlined
              hide-details
              @change="applyFilters"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="safeFilters.level"
              :items="levelOptions"
              label="Nível"
              clearable
              @change="applyFilters"
            ></v-select>
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="safeFilters.method"
              :items="methodOptions"
              label="Método"
              clearable
              @change="applyFilters"
            ></v-select>
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="safeFilters.error_type"
              :items="errorTypeOptions"
              label="Tipo de Erro"
              clearable
              @change="applyFilters"
            ></v-select>
          </v-col>
          <v-col cols="12" md="3">
            <v-checkbox
              v-model="safeFilters.stuck_only"
              label="Apenas travados"
              @change="applyFilters"
            ></v-checkbox>
          </v-col>
        </v-row>
      </v-card>

      <!-- Lista de Logs -->
      <v-card>
        <v-card-title>
          Logs de Erro e Warning
          <v-spacer></v-spacer>
          <v-btn icon @click="loadLogs" :loading="loading">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </v-card-title>

        <!-- Mensagem quando não há logs -->
        <v-card-text v-if="!loading && safeErrorLogs.length === 0" class="text-center pa-5">
          <v-icon large color="grey lighten-1">mdi-information-outline</v-icon>
          <p class="mt-3 grey--text">Nenhum log de erro encontrado.</p>
          <p class="caption grey--text">Os logs de erro e warning aparecerão aqui quando ocorrerem problemas nas execuções.</p>
        </v-card-text>

        <!-- Mensagem de erro -->
        <v-card-text v-if="errorMessage" class="text-center pa-5">
          <v-alert type="error" dismissible @input="errorMessage = null">
            {{ errorMessage }}
          </v-alert>
        </v-card-text>

        <v-data-table
          v-if="!loading && safeErrorLogs.length > 0"
          :headers="headers"
          :items="safeErrorLogs"
          :items-per-page="50"
          :page="safePagination.current_page"
          hide-default-footer
          class="elevation-1"
        >
          <template v-slot:item.level="{ item }">
            <v-chip
              v-if="item && item.level"
              :color="item.level === 'error' ? 'red' : 'orange'"
              dark
              small
            >
              {{ item.level === 'error' ? 'ERRO' : 'WARN' }}
            </v-chip>
            <span v-else>N/A</span>
          </template>

          <template v-slot:item.created_at="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <template v-slot:item.message="{ item }">
            <div v-if="item && item.message" class="text-truncate" style="max-width: 400px;" :title="item.message">
              {{ item.message }}
            </div>
            <span v-else>N/A</span>
          </template>

          <template v-slot:item.execution_status="{ item }">
            <v-chip
              v-if="item && item.execution_status"
              :color="getStatusColor(item.execution_status)"
              dark
              small
            >
              {{ item.execution_status }}
            </v-chip>
            <span v-else>N/A</span>
            <v-chip
              v-if="item && item.is_stuck"
              color="purple"
              dark
              small
              class="ml-2"
            >
              TRAVADO
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn
              small
              color="primary"
              text
              @click="showDetails(item)"
            >
              Mostrar os detalhes
            </v-btn>
          </template>
        </v-data-table>

        <!-- Dialog com detalhes completos do log -->
        <v-dialog v-model="detailsDialog" max-width="980" scrollable persistent>
          <v-card>
            <v-card-title class="headline grey lighten-2">
              Detalhes do log
              <v-spacer></v-spacer>
              <v-btn icon @click="detailsDialog = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text class="pt-4" v-if="selectedLog">
              <!-- Metadados em cards compactos -->
              <v-row dense class="mb-3">
                <v-col cols="6" sm="4" md="3">
                  <v-card outlined flat class="pa-2" style="min-height: 0;">
                    <div class="text-caption grey--text">ID</div>
                    <div class="text-body-2">{{ selectedLog.id }}</div>
                  </v-card>
                </v-col>
                <v-col cols="6" sm="4" md="3">
                  <v-card outlined flat class="pa-2" style="min-height: 0;">
                    <div class="text-caption grey--text">Data/Hora</div>
                    <div class="text-body-2">{{ formatDate(selectedLog.created_at) }}</div>
                  </v-card>
                </v-col>
                <v-col cols="6" sm="4" md="3">
                  <v-card outlined flat class="pa-2" style="min-height: 0;">
                    <div class="text-caption grey--text">Nível</div>
                    <div class="text-body-2">{{ selectedLog.level }}</div>
                  </v-card>
                </v-col>
                <v-col cols="6" sm="4" md="3">
                  <v-card outlined flat class="pa-2" style="min-height: 0;">
                    <div class="text-caption grey--text">Método</div>
                    <div class="text-body-2 text-truncate" :title="selectedLog.method">{{ selectedLog.method || '—' }}</div>
                  </v-card>
                </v-col>
                <v-col cols="6" sm="4" md="3">
                  <v-card outlined flat class="pa-2" style="min-height: 0;">
                    <div class="text-caption grey--text">Tipo</div>
                    <div class="text-body-2 text-truncate" :title="selectedLog.error_type">{{ selectedLog.error_type || '—' }}</div>
                  </v-card>
                </v-col>
                <v-col cols="6" sm="4" md="3">
                  <v-card outlined flat class="pa-2" style="min-height: 0;">
                    <div class="text-caption grey--text">Node Exec ID</div>
                    <div class="text-body-2">{{ selectedLog.node_execution_id || '—' }}</div>
                  </v-card>
                </v-col>
                <v-col cols="6" sm="4" md="3">
                  <v-card outlined flat class="pa-2" style="min-height: 0;">
                    <div class="text-caption grey--text">Execution ID</div>
                    <div class="text-body-2">{{ selectedLog.execution_id || '—' }}</div>
                  </v-card>
                </v-col>
                <v-col cols="6" sm="4" md="3">
                  <v-card outlined flat class="pa-2" style="min-height: 0;">
                    <div class="text-caption grey--text">Status exec.</div>
                    <div class="text-body-2">{{ selectedLog.execution_status || '—' }}</div>
                  </v-card>
                </v-col>
                <v-col cols="6" sm="4" md="3">
                  <v-card outlined flat class="pa-2" style="min-height: 0;">
                    <div class="text-caption grey--text">Status workflow</div>
                    <div class="text-body-2">{{ selectedLog.workflow_status || '—' }}</div>
                  </v-card>
                </v-col>
                <v-col cols="6" sm="4" md="3">
                  <v-card outlined flat class="pa-2" style="min-height: 0;">
                    <div class="text-caption grey--text">Travado</div>
                    <div class="text-body-2">{{ selectedLog.is_stuck ? 'Sim' : 'Não' }}</div>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Mensagem em destaque -->
              <div class="mb-3">
                <div class="text-caption grey--text mb-1">Mensagem</div>
                <v-card outlined class="pa-3" style="max-height: 140px; overflow-y: auto;">
                  <div class="text-body-2" style="white-space: pre-wrap; word-break: break-word;">{{ selectedLog.message || '—' }}</div>
                </v-card>
              </div>

              <!-- Detalhes JSON recolhível -->
              <v-expansion-panels v-if="selectedLog.error_details" dense class="mb-3">
                <v-expansion-panel>
                  <v-expansion-panel-header class="py-2">Detalhes (JSON)</v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <pre class="pa-2 ma-0 text-body-2" style="background: #f5f5f5; overflow-x: auto; max-height: 220px; overflow-y: auto;">{{ formatErrorDetails(selectedLog.error_details) }}</pre>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>

              <!-- Análise automática para suporte -->
              <v-divider class="my-4"></v-divider>
              <div v-if="selectedLogAnalysis" class="analysis-box">
                <div class="text-subtitle-1 font-weight-bold mb-2">Análise para o suporte</div>
                <v-alert type="info" dense outlined class="mb-2">
                  <div class="font-weight-medium">O que ocorreu</div>
                  <div class="text-body-2">{{ selectedLogAnalysis.what_happened }}</div>
                </v-alert>
                <v-alert type="warning" dense outlined class="mb-2">
                  <div class="font-weight-medium">Causa provável</div>
                  <div class="text-body-2">{{ selectedLogAnalysis.probable_cause }}</div>
                </v-alert>
                <v-alert type="success" dense outlined>
                  <div class="font-weight-medium">Ação sugerida</div>
                  <div class="text-body-2">{{ selectedLogAnalysis.suggested_action }}</div>
                </v-alert>
              </div>
              <div v-else-if="analysisError" class="text-caption grey--text">{{ analysisError }}</div>
              <div v-else-if="analysisLoading" class="text-caption">Carregando análise...</div>

              <!-- Agente Ask: pergunta livre -->
              <v-divider class="my-4"></v-divider>
              <div class="agent-ask">
                <div class="text-subtitle-1 font-weight-bold mb-2">Perguntar ao agente</div>
                <p class="text-caption grey--text mb-2">Faça uma pergunta sobre este log. O agente responde com análise em português (IA quando configurada, senão regras).</p>
                <v-textarea
                  v-model="agentQuestion"
                  placeholder="Ex: O que fazer para corrigir? Por que deu timeout? Como evitar esse erro?"
                  rows="2"
                  outlined
                  dense
                  hide-details
                  class="mb-2"
                ></v-textarea>
                <v-btn color="primary" :loading="agentLoading" :disabled="!agentConfigured || !selectedLog || !selectedLog.id" @click="askAgent">
                  Enviar ao agente
                </v-btn>
                <span v-if="agentConfigured === false" class="ml-3 text-caption grey--text">Agente de IA não configurado</span>
                <div v-if="agentError" class="mt-2 text-caption error--text">{{ agentError }}</div>
                <v-alert v-if="agentAnswer" type="info" dense outlined class="mt-3 text-left">
                  <div class="text-caption grey--text mb-1">
                    {{ agentAnswerSource === 'llm' ? 'Resposta do agente (IA)' : 'Resposta do agente (análise por regras)' }}
                  </div>
                  <div class="text-body-2" style="white-space: pre-wrap;">{{ agentAnswer }}</div>
                </v-alert>
              </div>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" text @click="detailsDialog = false">Fechar</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Paginação -->
        <div v-if="!loading && safeErrorLogs.length > 0 && safePagination.last_page > 1" class="text-center pa-4">
          <v-pagination
            v-model="safePagination.current_page"
            :length="safePagination.last_page"
            :total-visible="7"
            @input="changePage"
          ></v-pagination>
          <p class="caption mt-2 grey--text">
            Mostrando {{ (safePagination.current_page - 1) * safePagination.per_page + 1 }} a 
            {{ Math.min(safePagination.current_page * safePagination.per_page, safePagination.total) }} 
            de {{ safePagination.total }} logs
          </p>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'ErrorLogs',
  data() {
    return {
      errorMessage: null,
      detailsDialog: false,
      selectedLog: null,
      selectedLogAnalysis: null,
      analysisLoading: false,
      analysisError: null,
      agentQuestion: '',
      agentAnswer: null,
      agentAnswerSource: null,
      agentLoading: false,
      agentError: null,
      agentConfigured: null,
      periodPreset: 'today',
      periodOptions: [
        { text: 'Hoje', value: 'today' },
        { text: 'Últimos 7 dias', value: 'last7' },
        { text: 'Últimos 30 dias', value: 'last30' },
        { text: 'Personalizado', value: 'custom' }
      ],
      headers: [
        { text: 'Nível', value: 'level', sortable: true },
        { text: 'Data/Hora', value: 'created_at', sortable: true },
        { text: 'Método', value: 'method', sortable: true },
        { text: 'Tipo', value: 'error_type', sortable: true },
        { text: 'Mensagem', value: 'message', sortable: false },
        { text: 'Status Execução', value: 'execution_status', sortable: true },
        { text: 'Ações', value: 'actions', sortable: false }
      ],
      levelOptions: [
        { text: 'Erro', value: 'error' },
        { text: 'Warning', value: 'warn' }
      ],
      methodOptions: [],
      errorTypeOptions: [
        { text: 'IDs do formulário', value: 'form_ids' },
        { text: 'Timeout', value: 'timeout' },
        { text: 'Elemento não encontrado', value: 'element_not_found' },
        { text: 'Erro de sintaxe', value: 'syntax_error' },
        { text: 'Erro de tipo', value: 'type_error' },
        { text: 'Erro de rede', value: 'network_error' },
        { text: 'Máximo de tentativas', value: 'max_attempts' },
        { text: 'Erro de execução', value: 'execution_error' },
        { text: 'Desconhecido', value: 'unknown' }
      ]
    };
  },
  computed: {
    ...mapGetters('errorLogs', ['errorLogs', 'loading', 'pagination', 'filters', 'stats']),
    // Garantir que sempre temos arrays/objetos válidos
    safeErrorLogs() {
      return Array.isArray(this.errorLogs) ? this.errorLogs : [];
    },
    safePagination() {
      return this.pagination || {
        current_page: 1,
        last_page: 1,
        per_page: 50,
        total: 0
      };
    },
    safeFilters() {
      return this.filters || {
        level: null,
        method: null,
        error_type: null,
        date_from: null,
        date_to: null,
        stuck_only: false
      };
    }
  },
  mounted() {
    this.setPeriodDates('today');
    this.fetchAgentStatus();
    // Garantir que o Vue aplicou os filtros antes de carregar (evita "Hoje" vir vazio)
    this.$nextTick(() => {
      this.loadLogs();
      this.loadStats();
    });
  },
  methods: {
    ...mapActions('errorLogs', ['GET_ERROR_LOGS', 'GET_ERROR_LOGS_STATS', 'SET_FILTERS', 'SET_PAGE']),
    
    async loadLogs() {
      try {
        this.errorMessage = null;
        console.log('[ErrorLogs Component] Iniciando carregamento de logs...');
        console.log('[ErrorLogs Component] Estado atual do Vuex:', this.$store.state.errorLogs);
        console.log('[ErrorLogs Component] GET_ERROR_LOGS disponível?', typeof this.GET_ERROR_LOGS);
        
        // Verificar se a action existe
        if (typeof this.GET_ERROR_LOGS !== 'function') {
          const errorMsg = 'Erro: GET_ERROR_LOGS não está disponível. Verifique se o módulo está registrado corretamente.';
          console.error('[ErrorLogs Component]', errorMsg);
          this.errorMessage = errorMsg;
          return;
        }
        
        // Verificar token de autenticação
        if (!localStorage.access_token) {
          const errorMsg = 'Erro: Não autenticado. Faça login novamente.';
          console.error('[ErrorLogs Component]', errorMsg);
          this.errorMessage = errorMsg;
          return;
        }
        
        console.log('[ErrorLogs Component] Chamando GET_ERROR_LOGS...');
        console.log('[ErrorLogs Component] this.GET_ERROR_LOGS:', this.GET_ERROR_LOGS);
        console.log('[ErrorLogs Component] this.$store._actions:', Object.keys(this.$store._actions));
        
        // Chamar diretamente via dispatch para garantir que execute
        try {
          console.log('[ErrorLogs Component] Chamando via dispatch...');
          const result = await this.$store.dispatch('errorLogs/GET_ERROR_LOGS');
          console.log('[ErrorLogs Component] GET_ERROR_LOGS retornou:', result);
        } catch (error) {
          console.error('[ErrorLogs Component] Erro ao chamar GET_ERROR_LOGS:', error);
          console.error('[ErrorLogs Component] Stack:', error.stack);
          this.errorMessage = `Erro ao carregar logs: ${error.message || 'Erro desconhecido'}`;
          return;
        }
        
        // Aguardar um pouco para garantir que o Vuex atualizou
        await this.$nextTick();
        
        console.log('[ErrorLogs Component] Estado do Vuex após requisição:', this.$store.state.errorLogs);
        console.log('[ErrorLogs Component] this.errorLogs (getter):', this.errorLogs);
        console.log('[ErrorLogs Component] Tipo de errorLogs:', typeof this.errorLogs, Array.isArray(this.errorLogs));
        console.log('[ErrorLogs Component] Total de logs:', this.errorLogs?.length || 0);
        
        // Atualiza lista de métodos disponíveis
        if (Array.isArray(this.errorLogs) && this.errorLogs.length > 0) {
          const methods = [...new Set(this.errorLogs.map(log => log && log.method).filter(Boolean))];
          this.methodOptions = methods.map(m => ({ text: m, value: m }));
          console.log('[ErrorLogs Component] Métodos encontrados:', this.methodOptions);
        } else {
          this.methodOptions = [];
          console.log('[ErrorLogs Component] Nenhum log encontrado ou errorLogs não é array');
        }
      } catch (error) {
        console.error('[ErrorLogs Component] Erro ao carregar logs:', error);
        console.error('[ErrorLogs Component] Stack:', error.stack);
        console.error('[ErrorLogs Component] Erro completo:', JSON.stringify(error, null, 2));
        
        // Mostrar mensagem de erro amigável
        if (error.response) {
          if (error.response.status === 401) {
            this.errorMessage = 'Erro de autenticação. Faça login novamente.';
          } else if (error.response.status === 404) {
            this.errorMessage = 'Endpoint não encontrado. Verifique se a API está configurada corretamente.';
          } else {
            this.errorMessage = `Erro ao carregar logs: ${error.response.status} - ${error.response.data?.message || error.message}`;
          }
        } else if (error.message) {
          this.errorMessage = `Erro ao carregar logs: ${error.message}`;
        } else {
          this.errorMessage = 'Erro desconhecido ao carregar logs. Verifique o console para mais detalhes.';
        }
        
        this.methodOptions = [];
      }
    },
    
    async loadStats() {
      await this.GET_ERROR_LOGS_STATS();
    },
    
    applyFilters() {
      this.SET_FILTERS(this.safeFilters);
      this.SET_PAGE(1);
      this.loadLogs();
    },

    onPeriodChange(value) {
      if (value !== 'custom') {
        this.setPeriodDates(value);
        this.applyFilters();
      }
    },

    setPeriodDates(preset) {
      const today = new Date();
      // Usar data no fuso do usuário (não UTC) para "Hoje" bater com o dia local
      const formatLocal = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      };
      let dateFrom, dateTo;
      if (preset === 'today') {
        dateFrom = formatLocal(today);
        dateTo = formatLocal(today);
      } else if (preset === 'last7') {
        const from = new Date(today);
        from.setDate(from.getDate() - 7);
        dateFrom = formatLocal(from);
        dateTo = formatLocal(today);
      } else if (preset === 'last30') {
        const from = new Date(today);
        from.setDate(from.getDate() - 30);
        dateFrom = formatLocal(from);
        dateTo = formatLocal(today);
      } else {
        return;
      }
      this.SET_FILTERS({ ...this.safeFilters, date_from: dateFrom, date_to: dateTo });
    },
    
    changePage(page) {
      this.SET_PAGE(page);
      this.loadLogs();
    },
    
    formatDate(date) {
      if (!date) return 'N/A';
      const d = new Date(date);
      return d.toLocaleString('pt-BR');
    },
    
    getStatusColor(status) {
      const colors = {
        'running': 'blue',
        'finished': 'green',
        'error': 'red',
        'canceled': 'grey'
      };
      return colors[status] || 'grey';
    },

    async showDetails(item) {
      this.selectedLog = item;
      this.selectedLogAnalysis = null;
      this.analysisError = null;
      this.agentAnswer = null;
      this.agentAnswerSource = null;
      this.agentError = null;
      this.agentQuestion = '';
      this.detailsDialog = true;
      if (item && item.id) {
        this.analysisLoading = true;
        try {
          const { data } = await this.$http.get(`error-logs/${item.id}/analyze`, {
            headers: { Authorization: localStorage.access_token }
          });
          this.selectedLogAnalysis = data.analysis || null;
        } catch (e) {
          this.analysisError = e.response?.status === 404 ? 'Log não encontrado para análise.' : 'Não foi possível carregar a análise.';
        } finally {
          this.analysisLoading = false;
        }
      }
    },

    formatErrorDetails(details) {
      if (!details) return '—';
      return typeof details === 'object' ? JSON.stringify(details, null, 2) : String(details);
    },

    async fetchAgentStatus() {
      try {
        const { data } = await this.$http.get('error-logs/agent-status', {
          headers: { Authorization: localStorage.access_token }
        });
        this.agentConfigured = data.configured === true;
      } catch (_) {
        this.agentConfigured = false;
      }
    },

    async askAgent() {
      if (!this.selectedLog || !this.selectedLog.id) return;
      this.agentError = null;
      this.agentAnswer = null;
      this.agentLoading = true;
      try {
        const { data } = await this.$http.post('error-logs/ask', {
          log_id: this.selectedLog.id,
          question: this.agentQuestion || undefined
        }, {
          headers: { Authorization: localStorage.access_token }
        });
        this.agentAnswer = data.answer || '';
        this.agentAnswerSource = data.source || 'rules';
      } catch (e) {
        this.agentError = e.response?.data?.error || e.response?.status === 404 ? 'Log não encontrado.' : 'Não foi possível obter resposta do agente.';
      } finally {
        this.agentLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.error-logs {
  padding: 20px;
}

.titulo {
  padding: 15px;
  background-color: #f5f5f5;
  margin-bottom: 20px;
}
</style>

